// AssistantDrawer — the global platform-assistant surface (plan 2026-07-28 §5).
//
// A right-side drawer available on every authenticated page. Streams chat
// against the `assistant` surface and renders text answers, multiple-choice
// clarifications, and ActionProposalCards. The full ChatPage machinery
// (datasource pickers, routing banners, charts/tables) deliberately does NOT
// apply here — the assistant never renders data visualisations.
//
// Presentational: the chat state machine is owned by AssistantProvider and
// passed in (plan 2026-08-02), so the dashboard chat bar can start a turn in
// the same transcript. Do NOT call useAssistantChat() here — a second call site
// would create a second transcript competing for this drawer.
//
// Body layout (plan 2026-08-24) has two states, distinguished by
// `data-layout="hero" | "docked"` on the composer Box:
//   - "hero"   — no messages yet: the EmptyState block is vertically centered
//                inside the ScrollArea and the composer's top border is
//                dropped, so greeting + composer read as one centered group.
//   - "docked" — with messages (or while loading): transcript scrolls, the
//                composer sits in its bordered bottom slot.
// The composer Box NEVER changes position in the tree — only its styles switch.
// AssistantInput holds the draft in local state and the transcript is shared,
// so `messages.length` can flip 0→1 from outside this drawer while the user is
// typing; reparenting the input would remount it and discard that draft.
// Centering recipe: Mantine's ScrollArea viewport wraps children in a
// shrink-wrapping `content` div, so `margin-block: auto` alone resolves to 0 —
// the `content` Styles-API selector below (flat object; nested selectors are
// silently dropped) makes it a full-height flex column so the auto margins
// center, and still collapse to 0 on overflow in a short, resized drawer.
import { useEffect, useLayoutEffect, useRef, type CSSProperties } from "react";
import {
  ActionIcon,
  Box,
  Center,
  Drawer,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowLeft, IconHistory, IconPlus, IconSparkles } from "@tabler/icons-react";

import { OrbitalDots } from "@/components/loaders/OrbitalDots";
import type { AssistantChat } from "@/hooks/useAssistantChat";
import { useResizablePanel } from "@/hooks/useResizablePanel";
import { useAssistantName } from "./assistantBrand";
import { AssistantConversationList } from "./AssistantConversationList";
import { AssistantInput } from "./AssistantInput";
import { AssistantMessage } from "./AssistantMessage";
import { DRAWER_EXAMPLE_PROMPTS } from "./suggestions";
import styles from "./AssistantDrawer.module.css";
import { heroEntranceClasses } from "@/components/chat/HeroTraceOverlay";

interface Props {
  opened: boolean;
  onClose: () => void;
  chat: AssistantChat;
}

function EmptyState({ onPick }: { onPick: (prompt: string) => void }) {
  return (
    <Stack gap="lg" align="center" px="md" pt="xl">
      <Stack gap={4} align="center">
        <IconSparkles size={28} color="var(--mantine-color-blue-6)" />
        <Text fw={600}>How can I help?</Text>
        <Text size="sm" c="dimmed" ta="center">
          I can set things up for you (with your confirmation), check your data,
          and explain how features work.
        </Text>
      </Stack>
      <Stack gap="xs" w="100%">
        {DRAWER_EXAMPLE_PROMPTS.map((prompt) => (
          <UnstyledButton
            key={prompt}
            onClick={() => onPick(prompt)}
            p="sm"
            style={{
              borderRadius: 8,
              border: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Text size="sm">{prompt}</Text>
          </UnstyledButton>
        ))}
      </Stack>
    </Stack>
  );
}

export function AssistantDrawer({ opened, onClose, chat }: Props) {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const viewportRef = useRef<HTMLDivElement>(null);
  const { size, getHandleProps } = useResizablePanel();
  const assistantName = useAssistantName();

  // Width comes from the drawer `size` prop. A custom height is applied via the
  // Drawer's `--drawer-height` CSS var, and `--drawer-align: flex-end` docks the
  // shortened panel to the BOTTOM-right. Full height (height === null) leaves
  // Mantine's defaults untouched. Handles are desktop-only — mobile stays 100%.
  const rootStyles =
    !isMobile && size.height != null
      ? {
          root: {
            "--drawer-height": `${size.height}px`,
            "--drawer-align": "flex-end",
          } as CSSProperties,
        }
      : undefined;

  // Keep the transcript pinned to the bottom as messages stream in.
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages, chat.isStreaming]);

  // Scroll to the bottom whenever the drawer (re)opens on the chat view.
  useEffect(() => {
    if (opened && chat.view === "chat") {
      const el = viewportRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [opened, chat.view]);

  const lastMsg = chat.messages[chat.messages.length - 1];
  const showThinking =
    chat.isStreaming && (!lastMsg || (lastMsg.sender === "ai" && !lastMsg.content));

  // Empty conversation → hero layout (see the header comment). Loading and the
  // history view keep the docked layout so nothing jumps while messages arrive.
  const isHero =
    chat.view !== "history" && !chat.isLoadingMessages && chat.messages.length === 0;

  return (
    <Drawer.Root
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? "100%" : size.width}
      styles={rootStyles}
    >
      <Drawer.Overlay backgroundOpacity={0.35} blur={1} />
      {/* NEVER use the plain `style` prop here: Mantine applies Drawer.Content's
          `style` to BOTH the content and the fixed inner wrapper
          (DrawerContent.mjs getStyles("inner", { style })) — a leaked
          `position: relative` knocks the inner out of position:fixed and drops
          the whole drawer below the viewport. The Styles API is per-element. */}
      <Drawer.Content
        styles={{
          content: { display: "flex", flexDirection: "column", position: "relative" },
        }}
      >
        {!isMobile && (
          <>
            <div
              className={`${styles.handle} ${styles.top}`}
              title="Drag to resize · double-click to reset height"
              aria-hidden
              {...getHandleProps("y")}
            />
            <div
              className={`${styles.handle} ${styles.left}`}
              title="Drag to resize · double-click to reset width"
              aria-hidden
              {...getHandleProps("x")}
            />
            <div
              className={`${styles.handle} ${styles.corner}`}
              title="Drag to resize · double-click to reset"
              aria-hidden
              {...getHandleProps("both")}
            />
          </>
        )}
        <Drawer.Header>
          <Group justify="space-between" w="100%" wrap="nowrap">
            <Group gap="xs" wrap="nowrap">
              {chat.view === "history" ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => chat.setView("chat")}
                  aria-label="Back to conversation"
                >
                  <IconArrowLeft size={18} />
                </ActionIcon>
              ) : (
                <IconSparkles size={20} color="var(--mantine-color-blue-6)" />
              )}
              <Text fw={700}>
                {assistantName ? `Ask your ${assistantName} assistant` : "Ask your assistant"}
              </Text>
            </Group>
            <Group gap={2} wrap="nowrap">
              <Tooltip label="Conversations" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="Conversations"
                  onClick={() => chat.setView(chat.view === "history" ? "chat" : "history")}
                >
                  <IconHistory size={18} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="New conversation" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="New conversation"
                  onClick={chat.newConversation}
                >
                  <IconPlus size={18} />
                </ActionIcon>
              </Tooltip>
              <Drawer.CloseButton />
            </Group>
          </Group>
        </Drawer.Header>

        <Drawer.Body
          p={0}
          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          {chat.view === "history" ? (
            <Box p="md" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <AssistantConversationList
                conversations={chat.conversations}
                activeConversationId={chat.activeConversationId}
                onSelect={chat.selectConversation}
                onNew={chat.newConversation}
              />
            </Box>
          ) : (
            <>
              <ScrollArea
                style={{ flex: 1, minHeight: 0 }}
                viewportRef={viewportRef}
                styles={{
                  content: { minHeight: "100%", display: "flex", flexDirection: "column" },
                }}
              >
                {chat.isLoadingMessages ? (
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                ) : chat.messages.length === 0 ? (
                  // `marginTop: auto` anchors the block to the bottom of the
                  // scroll area, directly above the composer — together with
                  // the bottom spacer below the composer they center as ONE
                  // group (Gemini/ChatGPT pattern). The auto margin still
                  // collapses to 0 on overflow, so a short drawer stays
                  // scrollable from the top.
                  <Box
                    className={heroEntranceClasses.heroIntro}
                    style={{ marginTop: "auto" }}
                  >
                    <EmptyState onPick={(p) => chat.send(p)} />
                  </Box>
                ) : (
                  <Stack gap="md" p="md">
                    {chat.messages.map((msg) => (
                      <AssistantMessage
                        key={msg.id}
                        msg={msg}
                        onSelectOption={chat.selectMultipleChoice}
                        onRetry={chat.retry}
                        busy={chat.isStreaming}
                      />
                    ))}
                    {showThinking && (
                      <Group gap="xs">
                        <OrbitalDots size={22} />
                        <Text size="sm" c="dimmed">
                          {chat.statusLabel
                            ? chat.statusLabel.replace(/\.\.\.$/, "…")
                            : "Thinking…"}
                        </Text>
                      </Group>
                    )}
                  </Stack>
                )}
              </ScrollArea>

              {/* Fixed positional slot in BOTH layouts — only the styles
                  switch. See the header comment: remounting AssistantInput
                  would discard the user's in-progress draft. */}
              <Box
                p="md"
                // Same rise as the EmptyState block above — one unit, once.
                className={isHero ? heroEntranceClasses.heroIntro : undefined}
                data-layout={isHero ? "hero" : "docked"}
                style={{
                  borderTop: isHero
                    ? undefined
                    : "1px solid var(--mantine-color-default-border)",
                }}
              >
                {chat.loadError && (
                  <Text size="xs" c="red" mb="xs">
                    {chat.loadError}
                  </Text>
                )}
                <AssistantInput
                  onSend={(text) => chat.send(text)}
                  onStop={chat.abort}
                  isStreaming={chat.isStreaming}
                  hero={isHero}
                />
              </Box>

              {/* Bottom spacer (hero only): slightly larger than the scroll
                  area's flex so the centered group lands a bit above the true
                  middle. Conditional sibling AFTER the composer — its
                  positional slot stays stable. */}
              {isHero && (
                <Box style={{ flex: 1.25 }} data-testid="hero-bottom-spacer" />
              )}
            </>
          )}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
}
